import React, { useEffect, useMemo } from 'react'
import { format, startOfDay, endOfDay, startOfMonth, endOfMonth, compareAsc } from 'date-fns'
import { useEventsStore } from '../../stores/eventsStore'
import { useUserPreferencesStore } from '../../stores/userPreferencesStore'
import { getColorScheme } from '../../utils/colorSchemes'
import { getTypeColor } from '../../utils/eventTypeColors'
import { usePermissions } from '../../hooks/usePermissions'
import { useFamilyStore } from '../../stores/familyStore'

interface DayEventsListProps {
  date: Date
  childId: string
  onAddEvent?: (defaultDate: Date) => void
  onEditEvent?: (eventId: string, defaultDate: Date) => void
}

export const DayEventsList: React.FC<DayEventsListProps> = ({ date, childId, onEditEvent }) => {
  const { events, fetchEvents, deleteEvent } = useEventsStore()
  const scheme = useUserPreferencesStore(state => getColorScheme(state.preferences.color_scheme))
  const { isObserver } = usePermissions()
  const { family, members } = useFamilyStore()

  const startISO = useMemo(() => startOfDay(date).toISOString(), [date])
  const endISO = useMemo(() => endOfDay(date).toISOString(), [date])
  const monthStartISO = useMemo(() => startOfMonth(date).toISOString(), [date])
  const monthEndISO = useMemo(() => endOfMonth(date).toISOString(), [date])

  useEffect(() => {
    if (childId) fetchEvents(childId, monthStartISO, monthEndISO)
  }, [childId, monthStartISO, monthEndISO])

  const dayEvents = useMemo(() => {
    const typeOrder: Record<string, number> = { note: 0, expense: 1, event: 0 }
    return events
      .filter(ev => ev.child_id === childId && ev.start_at >= startISO && ev.start_at <= endISO)
      .sort((a, b) => {
        const t = (typeOrder[a.type as keyof typeof typeOrder] ?? 99) - (typeOrder[b.type as keyof typeof typeOrder] ?? 99)
        if (t !== 0) return t
        return compareAsc(new Date(a.start_at), new Date(b.start_at))
      })
  }, [events, childId, startISO, endISO])

  // Map user_id -> role and home label
  const userIdToHomeLabel = useMemo(() => {
    const map = new Map<string, string>()
    for (const m of members || []) {
      if (!m.user_id) continue
      if (m.role === 'parent_a') {
        map.set(m.user_id, family?.home_a_name || 'Home A')
      } else if (m.role === 'parent_b') {
        map.set(m.user_id, family?.home_b_name || 'Home B')
      }
    }
    return map
  }, [members, family])

  return (
    <div className="mt-2 space-y-2">
      {dayEvents.length === 0 ? (
        <div className="text-sm text-gray-500">No events for this day.</div>
      ) : (
        dayEvents.map(ev => {
          const createdByLabel = userIdToHomeLabel.get(ev.created_by) || '—'
          return (
            <div key={ev.id} className="bg-white rounded-lg border-2 border-slate-300 shadow p-3 pl-4 hover:shadow-md transition-shadow" style={{ borderLeftWidth: 6, borderLeftColor: getTypeColor(ev.type as any, scheme.id) }}>
              <div className="flex w-full items-stretch gap-3">
                {/* Left part: 4/5 width */}
                <div className="w-4/5">
                  {/* Row 1: Type, title, amount (for expense) */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border text-white"
                      title={ev.type === 'event' ? 'note' : ev.type}
                      style={{ backgroundColor: getTypeColor(ev.type as any, scheme.id), borderColor: getTypeColor(ev.type as any, scheme.id) }}
                    >
                      {ev.type === 'expense' ? 'Expense' : 'Note'}
                    </span>
                    <span className="font-medium text-slate-800">{ev.title}</span>
                    {ev.type === 'expense' && ev.amount != null && (
                      <span className="ml-2 text-sm font-semibold text-slate-700">{Number(ev.amount).toFixed(2)} {ev.currency || 'USD'}</span>
                    )}
                  </div>
                  {/* Row 2: Time and date */}
                  <div className="text-xs text-slate-700 mb-1">
                    {ev.all_day ? (
                      <>
                        <span className="font-semibold">All day</span>
                        {` • ${format(new Date(ev.start_at), 'dd MMM yyyy')}`}
                      </>
                    ) : (
                      <>
                        <span className="font-semibold">{`${format(new Date(ev.start_at), 'HH:mm')}${ev.end_at ? `–${format(new Date(ev.end_at), 'HH:mm')}` : ''}`}</span>
                        {` • ${format(new Date(ev.start_at), 'dd MMM yyyy')}`}
                      </>
                    )}
                  </div>
                  {/* Row 3: Location (only for note) */}
                  {ev.type !== 'expense' && ev.location ? (
                    <div className="text-xs text-slate-700 mb-1">Location: <span className="font-semibold">{ev.location}</span></div>
                  ) : null}
                  {/* Row 4: Note (if exists) */}
                  {ev.notes ? (
                    <div className="text-xs font-semibold text-slate-700 mb-1">{ev.notes}</div>
                  ) : null}
                  {/* Row 5: Author */}
                  <div className="text-xs text-slate-700">Created by: <span className="font-semibold">{createdByLabel}</span></div>
                </div>
                {/* Right part: 1/5 width – buttons vertically */}
                {!isObserver && (
                  <div className="w-1/5 shrink-0 flex flex-col items-stretch justify-start gap-2">
                    {onEditEvent && (
                      <button onClick={() => onEditEvent(ev.id, new Date(ev.start_at))} className="px-3 py-1 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">Edit</button>
                    )}
                    <button onClick={() => deleteEvent(ev.id)} className="px-3 py-1 text-xs font-medium rounded border border-red-500 bg-white text-red-600 hover:bg-red-50">Delete</button>
                  </div>
                )}
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

export default DayEventsList
